import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { clearInviteCallbackState, supabase } from '../lib/supabaseClient';
import { redirectToLogin } from '../lib/authRedirect';
import BrandLogo from '../components/BrandLogo';

const MIN_PASSWORD_LENGTH = 8;
const LOGIN_SUCCESS_NOTICE = 'Password set successfully. Please sign in with your new password.';

export default function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    user,
    initialAuthLoading,
    inviteCallbackState,
  } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [redirectTarget, setRedirectTarget] = useState('/login');

  const inviteRequested = searchParams.get('invite') === '1';
  const hasValidInviteSession = inviteRequested
    && inviteCallbackState === 'accepted'
    && Boolean(user)
    && Boolean(supabase);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => {
      navigate(redirectTarget, { replace: true });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [navigate, redirectTarget, success]);

  const validate = () => {
    if (!password) return 'Password is required.';
    if (password.length < MIN_PASSWORD_LENGTH) return 'Password must be at least 8 characters.';
    if (password !== confirmPassword) return 'Passwords do not match.';
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!hasValidInviteSession || !supabase) {
      setError('This invitation link is invalid or expired.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        throw updateError;
      }

      clearInviteCallbackState();
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((resolve) => window.setTimeout(resolve, 2000)),
      ]);
      await redirectToLogin(LOGIN_SUCCESS_NOTICE);
      return;
    } catch (err) {
      setError(err?.message || 'Unable to set your password.');
    } finally {
      setSubmitting(false);
    }
  };

  if (initialAuthLoading) {
    return (
      <div className="min-h-screen bg-navy-950 hero-grid-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-8 shadow-2xl text-center text-slate-300">
          Loading invitation...
        </div>
      </div>
    );
  }

  if (!hasValidInviteSession) {
    return (
      <div className="min-h-screen bg-navy-950 hero-grid-bg flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-8 shadow-2xl text-center">
          <BrandLogo variant="card" className="mx-auto mb-5" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-3">This invitation link is invalid or expired.</h1>
          <p className="text-sm text-slate-400 leading-relaxed">
            Return to the login page and request a new invite from your administrator if needed.
          </p>
          <div className="mt-6">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors"
            >
              Go to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy-950 hero-grid-bg flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-navy-900/70 backdrop-blur-xl p-8 shadow-2xl">
          <BrandLogo variant="card" className="mb-5" />
          <h1 className="font-display text-3xl font-bold tracking-tight text-white mb-2">Set your password</h1>
          <p className="text-sm text-slate-400 mb-6">
            Finish accepting your invite by choosing a password for your workspace account.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="set-password-error">
              {error}
            </div>
          )}

          {success ? (
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-4 text-emerald-300 text-sm" data-testid="set-password-success">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <div>Your password has been set successfully.</div>
                  <div className="mt-1 text-xs opacity-80">
                    Redirecting you to login...
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="set-password-form">
              <Field
                label="New password"
                value={password}
                onChange={setPassword}
                testid="set-password-input"
              />
              <Field
                label="Confirm password"
                value={confirmPassword}
                onChange={setConfirmPassword}
                testid="set-password-confirm-input"
              />

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-cyan-glow text-navy-950 font-semibold hover:bg-cyan-hover transition-colors disabled:opacity-60"
                data-testid="set-password-submit-btn"
              >
                {submitting ? 'Saving...' : <>Continue <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-slate-400 uppercase tracking-wider font-mono">{label}</span>
      <input
        type="password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required
        minLength={MIN_PASSWORD_LENGTH}
        className="mt-2 w-full bg-navy-950 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:border-cyan-glow/50 focus:outline-none focus:ring-2 focus:ring-cyan-glow/20 transition-colors"
        data-testid={testid}
      />
    </label>
  );
}
